import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { SlidersHorizontal, DollarSign, Package } from "lucide-react";
import { MultiSelectDropdown } from "@/components/MultiSelectDropdown";

// Stable ordering + display labels for markup category buckets.
const MARKUP_CATEGORY_LABELS = {
  vsc: "VSC",
  bundle: "Bundle & Ancillary",
  wear_guard: "Wear Guard",
  gap: "GAP",
  limited_warranty: "Limited Warranty",
  go_forever_lifetime: "GoForever Lifetime",
  go_certified_preowned: "GoCertified Preowned",
};

const CATEGORY_ORDER = [
  "vsc",
  "bundle",
  "wear_guard",
  "gap",
  "limited_warranty",
  "go_forever_lifetime",
  "go_certified_preowned",
];

/**
 * Proposal settings: per-category commission markups, term filter, and
 * product filter.
 * Products are grouped by display category; unavailable products (rates not
 * loaded) show a "Coming soon" badge but remain in the list for future rollout.
 * Commission markups are keyed on the product's `markup_category` — one input
 * per markup bucket (VSC, Bundle, GAP, etc.). If a category has no explicit
 * value it falls back to the proposal's global `markup`.
 */
export function ProposalSettings({ proposal, onChange }) {
  const [terms, setTerms] = useState([]);
  const [products, setProducts] = useState([]);
  const globalMarkup = proposal.markup ?? 200;
  const [categoryMarkups, setCategoryMarkups] = useState(
    () => ({ ...(proposal.category_markups || {}) })
  );
  const [selectedTerms, setSelectedTerms] = useState(proposal.selected_terms || [12, 18, 24, 30, 36, 48, 60, 72, 84, 96, 120]);
  const [selectedProducts, setSelectedProducts] = useState(
    proposal.selected_products || ["essentials_powertrain", "bundle_4n1"]
  );
  const [gapSettings, setGapSettings] = useState(() => ({
    dealer_type: proposal.gap_settings?.dealer_type || "franchise",
    ltv: proposal.gap_settings?.ltv || "150",
  }));
  const [vscDealerType, setVscDealerType] = useState(proposal.vsc_dealer_type || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [t, p] = await Promise.all([
          api.get("/rates/terms"),
          api.get("/rates/products"),
        ]);
        setTerms(t.data.terms || []);
        setProducts(p.data.products || []);
      } catch (e) {
        console.error("Failed to load rates/products", e);
      }
    })();
  }, []);

  const commitDebounced = React.useRef(null);
  const persist = (nextCatMarkups, nextTerms, nextProducts, nextGap = gapSettings, nextVscDealer = vscDealerType) => {
    if (commitDebounced.current) clearTimeout(commitDebounced.current);
    commitDebounced.current = setTimeout(async () => {
      setSaving(true);
      try {
        const { data } = await api.put(`/proposals/${proposal.id}/settings`, {
          category_markups: nextCatMarkups,
          selected_terms: nextTerms,
          selected_products: nextProducts,
          gap_settings: nextGap,
          vsc_dealer_type: nextVscDealer || "",
        });
        onChange && onChange(data);
      } catch (e) {
        console.error("Failed to save proposal settings", e);
        toast.error("Save failed");
      } finally {
        setSaving(false);
      }
    }, 400);
  };

  const toggleTerm = (t) => {
    const next = selectedTerms.includes(t)
      ? selectedTerms.filter((x) => x !== t)
      : [...selectedTerms, t].sort((a, b) => a - b);
    setSelectedTerms(next);
    persist(categoryMarkups, next, selectedProducts, gapSettings, vscDealerType);
  };

  const toggleProduct = (key, available) => {
    if (!available) {
      toast.info("Rates for this product are coming soon.");
      return;
    }
    const next = selectedProducts.includes(key)
      ? selectedProducts.filter((x) => x !== key)
      : [...selectedProducts, key];
    setSelectedProducts(next);
    persist(categoryMarkups, selectedTerms, next, gapSettings, vscDealerType);
  };

  const setCategoryMarkup = (catKey, raw) => {
    const clean = raw === "" ? undefined : Math.max(0, Math.round(Number(raw) || 0));
    const next = { ...categoryMarkups };
    if (clean === undefined) delete next[catKey];
    else next[catKey] = clean;
    setCategoryMarkups(next);
    persist(next, selectedTerms, selectedProducts, gapSettings, vscDealerType);
  };

  const setVscDealer = (dt) => {
    // Dealer type (franchise/independent) is now purely optional metadata —
    // it no longer gates which VSC options appear. Just persist the value.
    const next = dt === vscDealerType ? "" : dt;  // click again to clear
    setVscDealerType(next);
    persist(categoryMarkups, selectedTerms, selectedProducts, gapSettings, next);
  };

  const setGapDealerType = (dt) => {
    const next = { ...gapSettings, dealer_type: dt };
    // Ford FLEX has no LTV; BHPH is fixed at 125%.
    if (dt === "ford_flex") next.ltv = null;
    else if (dt === "bhph") next.ltv = "125";
    else if (!next.ltv) next.ltv = "150";
    setGapSettings(next);
    persist(categoryMarkups, selectedTerms, selectedProducts, next);
  };

  const setGapLtv = (v) => {
    const next = { ...gapSettings, ltv: v };
    setGapSettings(next);
    persist(categoryMarkups, selectedTerms, selectedProducts, next);
  };

  // Group products by display category, preserve first-seen order.
  // Show ALL VSC options regardless of dealer type (dealer type is optional
  // metadata now — it doesn't gate what's visible).
  const grouped = [];
  const catIdx = {};
  for (const p of products) {
    if (!(p.category in catIdx)) {
      catIdx[p.category] = grouped.length;
      grouped.push({ category: p.category, items: [] });
    }
    grouped[catIdx[p.category]].items.push(p);
  }

  // Validation: proposal must have at least one VSC product selected (either
  // a Preowned tier or a New tier). Shown as a soft banner near the products.
  const VSC_KEYS = new Set([
    "essentials_powertrain", "essentials_plus", "essentials_enhanced", "essentials_exclusionary",
    "franchise_exclusionary",
  ]);
  const hasVscSelected = selectedProducts.some((k) => VSC_KEYS.has(k) || String(k).startsWith("newcar_") || String(k).startsWith("blended_"));

  // Markup categories to display = those where the user has at least one
  // selected available product (VSC always shown so a proposal never has zero
  // markup rows to configure).
  const activeMarkupCats = useMemo(() => {
    const set = new Set();
    for (const p of products) {
      if (p.available && selectedProducts.includes(p.key)) {
        set.add(p.markup_category);
      }
    }
    if (set.size === 0) set.add("vsc");
    // Return in stable order
    return CATEGORY_ORDER.filter((k) => set.has(k));
  }, [products, selectedProducts]);

  return (
    <Card className="bg-white border border-[#E9ECEF] rounded-sm p-6 mb-6" data-testid="proposal-settings-panel">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={16} className="text-[#D4AF37]" />
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">
            Proposal settings
          </div>
        </div>
        {saving && <div className="text-xs text-[#6C757D]">Saving…</div>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
        {/* Per-category commission markups */}
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B] flex items-center gap-1.5">
            <DollarSign size={12} /> Commission markups
          </Label>
          <p className="text-xs text-[#6C757D] mt-1.5 mb-4 leading-relaxed">
            Set your commission per product category. Added on top of the base
            rate for every quote in that category.
          </p>

          <div className="space-y-3" data-testid="category-markups-list">
            {activeMarkupCats.map((catKey) => {
              const val = categoryMarkups[catKey];
              const inputVal = val == null ? "" : val;
              const placeholder = String(Math.round(globalMarkup || 0));
              return (
                <div key={catKey} data-testid={`category-markup-row-${catKey}`}>
                  <div className="flex items-baseline justify-between mb-1">
                    <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">
                      {MARKUP_CATEGORY_LABELS[catKey] || catKey}
                    </div>
                    {val == null && (
                      <div className="text-[9px] uppercase tracking-[0.14em] text-[#6C757D]">
                        default
                      </div>
                    )}
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6C757D] text-sm font-semibold">
                      $
                    </span>
                    <Input
                      type="number"
                      min="0"
                      step="25"
                      value={inputVal}
                      placeholder={placeholder}
                      onChange={(e) => setCategoryMarkup(catKey, e.target.value)}
                      className="pl-7 h-10 rounded-sm border-[#0B132B]/15 focus-visible:ring-[#D4AF37] font-mono"
                      data-testid={`category-markup-input-${catKey}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Products + Terms */}
        <div className="space-y-6">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B] flex items-center gap-1.5">
              <Package size={12} /> Products to quote
            </Label>
            <p className="text-xs text-[#6C757D] mt-1.5 mb-4 leading-relaxed">
              Open each program and tick every product to quote. Products marked
              <span className="font-semibold text-[#D4AF37]"> Soon </span>
              don't have rates loaded yet.
            </p>

            {/* Dealer type selector — optional metadata only. Doesn't gate
                VSC visibility anymore; the agent must pick at least one VSC
                product below regardless. */}
            <div className="mb-5 border border-[#E9ECEF] rounded-sm p-4 bg-white" data-testid="vsc-dealer-type-panel">
              <div className="flex items-baseline justify-between gap-4 mb-2">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">
                    Dealer Type <span className="text-[#6C757D] font-normal normal-case tracking-normal">(optional)</span>
                  </div>
                  <p className="text-[11px] text-[#6C757D] mt-1 leading-snug">
                    Tag whether this dealership is a Franchise or Independent operation. Purely informational — VSC products are picked below and are not gated by this choice.
                  </p>
                </div>
                {vscDealerType && (
                  <button
                    type="button"
                    onClick={() => setVscDealer(vscDealerType)}
                    className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D] hover:text-[#0B132B] shrink-0"
                    data-testid="vsc-dealer-type-clear"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                {[
                  { key: "franchise",   label: "Franchise Dealer",  hint: "Sells new vehicles from a specific OEM" },
                  { key: "independent", label: "Independent Dealer", hint: "Preowned inventory, mixed brands" },
                ].map((opt) => {
                  const on = vscDealerType === opt.key;
                  return (
                    <div
                      key={opt.key}
                      role="button"
                      tabIndex={0}
                      onClick={() => setVscDealer(opt.key)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setVscDealer(opt.key); }
                      }}
                      className={`px-4 py-3 rounded-sm border-2 transition-colors cursor-pointer ${
                        on ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#E9ECEF] hover:border-[#0B132B]/30 bg-white"
                      }`}
                      data-testid={`vsc-dealer-type-${opt.key}`}
                    >
                      <div className={`font-semibold text-sm ${on ? "text-[#0B132B]" : "text-[#0B132B]"}`}>
                        {opt.label}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#6C757D] mt-1">
                        → {opt.hint}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {!hasVscSelected && (
              <div className="mb-4 flex items-center gap-2 p-3 border-2 border-red-600/40 bg-red-50 rounded-sm" data-testid="vsc-required-banner">
                <span className="inline-flex items-center px-2 py-0.5 rounded-sm bg-red-600 text-white text-[9px] uppercase tracking-[0.14em] font-bold shrink-0">
                  Required
                </span>
                <div className="text-xs text-[#0B132B] leading-snug">
                  Pick at least one VSC option below — either a <span className="font-bold">New</span> or <span className="font-bold">Preowned</span> tier — to generate quotes.
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="product-pickers">
              {grouped.map((g) => {
                const slug = g.category.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                return (
                  <MultiSelectDropdown
                    key={g.category}
                    label={g.category}
                    testId={`product-picker-${slug}`}
                    placeholder="None selected"
                    options={g.items.map((p) => ({ key: p.key, label: p.label, hint: p.description, disabled: !p.available, badge: p.available ? null : "Soon" }))}
                    selected={selectedProducts}
                    onToggle={(key, o) => toggleProduct(key, !o.disabled)}
                    onSelectAll={(on) => {
                      const keys = g.items.filter((p) => p.available).map((p) => p.key);
                      const next = on ? Array.from(new Set([...selectedProducts, ...keys])) : selectedProducts.filter((k) => !keys.includes(k));
                      setSelectedProducts(next);
                      persist(categoryMarkups, selectedTerms, next, gapSettings, vscDealerType);
                    }}
                  />
                );
              })}
            </div>
            {selectedProducts.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-3" data-testid="selected-products-chips">
                {products.filter((p) => selectedProducts.includes(p.key)).map((p) => (
                  <button key={p.key} type="button" onClick={() => toggleProduct(p.key, p.available)} className="px-1.5 py-0.5 rounded-sm bg-[#0B132B] text-white text-[10px] hover:bg-[#1c2541]" data-testid={`product-toggle-${p.key}`} title="Remove">
                    {p.label} ×
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">
              Terms to display
            </Label>
            <p className="text-xs text-[#6C757D] mt-1.5 mb-3 leading-relaxed">
              Uncheck any term you don't want to show. Mileage caps come from the underlying rate sheet automatically.
              <span className="block mt-1 text-[11px] text-[#6C757D]">
                <span className="font-semibold text-[#0B132B]">Preowned VSC</span> covers 12-72 months · <span className="font-semibold text-[#0B132B]">New VSC</span> covers 12-96 months · <span className="font-semibold text-[#0B132B]">Essentials New Car</span> up to 120 months.
              </span>
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {terms.map((t) => {
                const on = selectedTerms.includes(t.term_months);
                return (
                  <div
                    key={t.term_months}
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleTerm(t.term_months)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleTerm(t.term_months);
                      }
                    }}
                    className={`flex flex-col items-center justify-center px-3 py-3 rounded-sm border-2 cursor-pointer transition-colors ${
                      on ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[#E9ECEF] hover:border-[#0B132B]/30 bg-white"
                    }`}
                    data-testid={`term-toggle-${t.term_months}`}
                  >
                    <Checkbox checked={on} tabIndex={-1} className="sr-only" />
                    <div className={`font-display font-black text-lg tracking-tight ${on ? "text-[#0B132B]" : "text-[#6C757D]"}`}>
                      {t.term_months}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#6C757D] mt-0.5">
                      months
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {selectedProducts.includes("gap") && (
            <div data-testid="gap-config-panel">
              <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">
                GAP configuration
              </Label>
              <p className="text-xs text-[#6C757D] mt-1.5 mb-3 leading-relaxed">
                GAP pricing depends on the dealer type and loan-to-value (LTV)
                you're presenting to the customer.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0B132B] mb-1">Dealer type</div>
                  <Select value={gapSettings.dealer_type} onValueChange={setGapDealerType}>
                    <SelectTrigger className="h-10 rounded-sm" data-testid="gap-dealer-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="franchise" data-testid="gap-dealer-type-franchise">New Franchise</SelectItem>
                      <SelectItem value="independent" data-testid="gap-dealer-type-independent">Independent</SelectItem>
                      <SelectItem value="bhph" data-testid="gap-dealer-type-bhph">Buy Here Pay Here</SelectItem>
                      <SelectItem value="ford_flex" data-testid="gap-dealer-type-ford_flex">Ford FLEX GAP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0B132B] mb-1">LTV</div>
                  <Select
                    value={gapSettings.ltv || ""}
                    onValueChange={setGapLtv}
                    disabled={gapSettings.dealer_type === "ford_flex" || gapSettings.dealer_type === "bhph"}
                  >
                    <SelectTrigger className="h-10 rounded-sm" data-testid="gap-ltv">
                      <SelectValue placeholder={
                        gapSettings.dealer_type === "ford_flex" ? "N/A for Ford FLEX" :
                        gapSettings.dealer_type === "bhph" ? "125% (fixed for BHPH)" : "Choose"
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="150" data-testid="gap-ltv-150">Unlimited / 150% LTV</SelectItem>
                      <SelectItem value="125" data-testid="gap-ltv-125">125% LTV</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
